import React, { useState } from "react";
import PlayerRow from "./PlayerRow";
import Dropdown from "../../components/Dropdown";
import TeamLogo from "./TeamLogo";
import { findCompatibleSlots } from "../formation";
import { chemAvailableFor } from "../formMods";

const POSITION_GROUPS = {
  All: null,
  GK: ["GK"],
  DEF: ["CB", "LB", "RB", "LWB", "RWB"],
  MID: ["CDM", "CM", "CAM", "LM", "RM"],
  ATT: ["LW", "RW", "ST", "CF", "LF", "RF"],
};

const SORT_OPTIONS = [
  { value: "PAC", label: "PAC" },
  { value: "SHO", label: "SHO" },
  { value: "PAS", label: "PAS" },
  { value: "DRI", label: "DRI" },
  { value: "DEF", label: "DEF" },
  { value: "PHY", label: "PHY" },
];

export default function TeamDisplay({
  team,
  selectedId,
  roster,
  slots,
  locked,
  onPlayerClick,
}) {
  const [positionFilter, setPositionFilter] = useState("All");
  const [nameQuery, setNameQuery] = useState("");
  const [sortBy, setSortBy] = useState("PAC");

  const filtered = team.players.filter((p) => {
    if (positionFilter !== "All") {
      const accepted = POSITION_GROUPS[positionFilter];
      const playerPositions = p.positions?.length ? p.positions : [p.pos];
      if (!playerPositions.some((pos) => accepted.includes(pos))) return false;
    }
    if (nameQuery.trim()) {
      const q = nameQuery.toLowerCase();
      if (!p.name.toLowerCase().includes(q)) return false;
    }
    return true;
  });

  const sorted = [...filtered].sort((a, b) => {
    if (sortBy === "overall") return b.overall - a.overall;
    if (sortBy === "age") return (a.age || 0) - (b.age || 0);
    return (b.attrs[sortBy] || 0) - (a.attrs[sortBy] || 0);
  });

  return (
    <div className="flex flex-col h-full min-h-0">
      {/* Team header */}
      <div
        className="rounded-2xl bg-zinc-900/50 border border-zinc-800 p-5 border-l-[3px] shrink-0"
        style={{ borderLeftColor: team.primary || "#10b981" }}
      >
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <TeamLogo teamId={team.teamId} club={team.club} size={36} />
            <h2 className="text-xl font-bold text-zinc-100 truncate">
              {team.club}
            </h2>
          </div>
          <span className="text-[10px] uppercase tracking-widest text-zinc-500 shrink-0">
            {team.players.length} players
          </span>
        </div>
        <div className="text-sm text-zinc-400 mt-1">
          {team.season}
          {/* <span className="text-zinc-700 mx-2">·</span>
          {team.fifaEdition} */}
        </div>
      </div>

      {/* Filters */}
      <div className="mt-3 space-y-2 shrink-0">
        <div className="flex items-center gap-1.5 flex-wrap">
          {Object.keys(POSITION_GROUPS).map((group) => (
            <button
              key={group}
              type="button"
              onClick={() => setPositionFilter(group)}
              className={`px-3 py-1 rounded-full text-xs font-semibold tracking-wider transition
                ${
                  positionFilter === group
                    ? "bg-emerald-500/15 border border-emerald-500/40 text-emerald-300"
                    : "bg-zinc-900/40 border border-zinc-800 text-zinc-400 hover:border-zinc-700 hover:text-zinc-200"
                }`}
            >
              {group}
            </button>
          ))}
        </div>

        <div className="flex items-center gap-2">
          <input
            type="text"
            value={nameQuery}
            onChange={(e) => setNameQuery(e.target.value)}
            placeholder="Search players…"
            className="flex-1 min-w-0 bg-zinc-900/60 border border-zinc-800 rounded-lg
                       px-3 py-2 text-sm text-zinc-200 placeholder:text-zinc-600
                       focus:outline-none focus:border-zinc-700 transition"
          />

          <div className="flex items-center gap-2 shrink-0">
            <span className="text-[10px] uppercase tracking-widest text-zinc-500">
              Sort
            </span>
            <Dropdown
              value={sortBy}
              options={SORT_OPTIONS}
              onChange={setSortBy}
            />
          </div>
        </div>
      </div>

      {/* List */}
      <div className="mt-3 flex-1 min-h-0 overflow-y-auto pr-1 space-y-2 scrollbar-fancy">
        {sorted.length === 0 ? (
          <div className="text-center text-sm text-zinc-500 py-10">
            No players match your filters
          </div>
        ) : (
          (() => {
            const rosterNames = new Set(
              Object.values(roster).map((rp) => rp.name),
            );
            return sorted.map((p) => {
              const disabled =
                locked ||
                rosterNames.has(p.name) ||
                findCompatibleSlots(p, roster, slots).length === 0;
              return (
                <PlayerRow
                  key={p.id}
                  player={p}
                  selected={selectedId === p.id}
                  disabled={disabled}
                  chemAvailable={chemAvailableFor(p, roster)}
                  onClick={onPlayerClick}
                />
              );
            });
          })()
        )}
      </div>
    </div>
  );
}
